'use client';
import { usePathname } from 'next/navigation';
import { useEffect, useState, type ReactNode } from 'react';

import { Drawer } from './Drawer';
import { Sidebar, type NavItem } from './Sidebar';
import { Topbar } from './Topbar';
import { VerifyEmailBanner } from './VerifyEmailBanner';

const COLLAPSE_STORAGE_KEY = 'leva-sidebar-collapsed';

interface Props {
  active?: string;
  navItems: NavItem[];
  user: { name: string; plan: string; quotaPct?: number; avatarUrl?: string | null };
  title?: string;
  topbarRight?: ReactNode;
  /** Slot inside the sidebar's user widget — typically a sign-out icon button. */
  userActions?: ReactNode;
  /** When set, the avatar + name area becomes a Link to this path (typically /profile). */
  userHref?: string;
  emailVerified?: boolean;
  /** Initial value from server-rendered cookie read, so the banner doesn't flash on hydration. */
  initialBannerDismissed?: boolean;
  onResendVerify?: () => Promise<void>;
  children: ReactNode;
}

function writeDismissCookie() {
  // 24h expiry instead of session-scoped: Chrome's "Continue where you left
  // off" keeps session cookies indefinitely across browser quits, so a
  // session cookie effectively becomes "permanent" for those users. 24h
  // strikes the balance — banner doesn't nag every page load, but reminds
  // the user daily until they verify.
  const secure = typeof window !== 'undefined' && window.location.protocol === 'https:';
  const flags = ['leva_verify_dismissed=1', 'path=/', 'SameSite=Lax', 'Max-Age=86400'];
  if (secure) flags.push('Secure');
  document.cookie = flags.join('; ');
}

function isHrefActive(pathname: string, href: string): boolean {
  if (href === '/') return pathname === '/';
  return pathname === href || pathname.startsWith(href + '/');
}

export function AppShell({
  active,
  navItems,
  user,
  title = 'Leva',
  topbarRight,
  userActions,
  userHref,
  emailVerified,
  initialBannerDismissed = false,
  onResendVerify,
  children,
}: Props) {
  const pathname = usePathname() ?? '';
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(false);

  // Hydrate from localStorage post-mount to avoid SSR/CSR markup mismatch —
  // the server has no window. First paint shows expanded; the effect snaps
  // to the persisted value on hydration.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const stored = window.localStorage.getItem(COLLAPSE_STORAGE_KEY);
    if (stored === 'true') setCollapsed(true);
  }, []);

  function toggleCollapsed() {
    setCollapsed((prev) => {
      const next = !prev;
      if (typeof window !== 'undefined') {
        window.localStorage.setItem(COLLAPSE_STORAGE_KEY, String(next));
      }
      return next;
    });
  }
  // Seed from server-read cookie so the first render matches post-hydration —
  // no banner flash on page load. Subsequent in-session dismissals update
  // this via setBannerDismissed.
  const [bannerDismissed, setBannerDismissed] = useState(initialBannerDismissed);

  const matchedItem = navItems.find((it) => it.href && isHrefActive(pathname, it.href));
  const computedActive = matchedItem?.id ?? active ?? 'home';
  // Title mirrors the active sidebar item's label so the topbar always names
  // the current page. Falls back to the prop (e.g. "Leva") on routes that
  // don't appear in the sidebar.
  const computedTitle = matchedItem?.label ?? title;

  return (
    // `fixed inset-0` pins the whole shell to the viewport edges so the body
    // never scrolls — even if some upstream CSS allows body overflow. The
    // sidebar is then in normal flex flow within this fixed-size container
    // (no sticky needed). The main content owns its own scroll via the
    // overflow-y-auto on `<main>`, which keeps the topbar pinned naturally
    // since it sits OUTSIDE the scroll container.
    <div className="fixed inset-0 flex">
      <Drawer open={drawerOpen} onClose={() => setDrawerOpen(false)} />
      <Sidebar
        open={drawerOpen}
        collapsed={collapsed}
        active={computedActive}
        navItems={navItems}
        user={user}
        onNavigate={() => setDrawerOpen(false)}
        onToggleCollapse={toggleCollapsed}
        userActions={userActions}
        userHref={userHref}
      />
      <div className="flex-1 min-w-0 flex flex-col">
        {emailVerified === false && !bannerDismissed && onResendVerify && (
          <VerifyEmailBanner
            emailVerified={false}
            onResend={onResendVerify}
            onDismiss={() => {
              setBannerDismissed(true);
              writeDismissCookie();
            }}
          />
        )}
        <Topbar title={computedTitle} onHamburger={() => setDrawerOpen(true)} right={topbarRight} />
        <main className="flex-1 overflow-y-auto p-6 lg:p-10 bg-cream">{children}</main>
      </div>
    </div>
  );
}
